import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  IconButton,
  styled,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { DatePicker } from '@mui/x-date-pickers';
import { ArrowBackIosNewIcon } from 'assets/icons';
import { DateRangePicker } from 'components/commons';
import { useHeaderContext } from 'components/providers';
import { PAGE_LIST } from 'constant/pages';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import {
  matchPath,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { DatePattern } from 'utils/constant';
import ja from 'utils/ja';

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  height: 'fit-content',
  width: '100%',
  backgroundColor: theme.palette.common.white,
  boxShadow: 'none',
  ...(open && {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

interface IAppBarProps {
  open: boolean;
  handleDrawerToggle: () => void;
}

interface AppBarItemType {
  type: string;
  title?: string;
  path: string;
}

const AppBar: React.FC<IAppBarProps> = ({ open, handleDrawerToggle }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [dateParams, setDateParams] = useState<string | null>(null);
  const { date, setDate, rangeDate, setRangeDate } = useHeaderContext();

  const appBarItems: AppBarItemType[] = [
    {
      type: 'list',
      title: ja.side_bar.visitor_list,
      path: PAGE_LIST.HOME.pathname(),
    },
    {
      type: 'dashboard',
      path: PAGE_LIST.DASHBOARD.pathname(),
    },
    {
      type: 'user_detail',
      title: ja.user_detail.title,
      path: PAGE_LIST.USER_DETAIL.pathname(),
    },
    {
      type: 'visit_detail',
      title: ja.visit_detail.title,
      path: PAGE_LIST.VISIT_DETAIL.pathname(),
    },
  ];
  const currentPath = location.pathname;

  const [currentPage, setCurrentPage] = useState<AppBarItemType>();

  useEffect(() => {
    setCurrentPage(
      appBarItems.find((item) => matchPath(item.path, currentPath))
    );
    setDateParams(searchParams.get('startTime'));
  }, [currentPath]);

  const changeRangeDate = (start: string, end: string) => {
    const datePick = { start: start, end: end };
    setRangeDate(datePick);
  };

  return (
    <StyledAppBar open={open}>
      <Toolbar
        sx={{
          '@supports (-webkit-touch-callout: none)': {
            mt: currentPage?.type === 'list' ? '22px' : '0px',
          },
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent:
            currentPage?.type === 'list' ? 'space-between' : 'flex-start',
          alignItems: 'center',
        }}
        disableGutters
      >
        {['list', 'dashboard'].includes(currentPage?.type ?? '') && (
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                pr: '10px',
              }}
            >
              <IconButton onClick={handleDrawerToggle}>
                <MenuIcon
                  sx={{
                    fill: theme.palette.primary.main,
                    fontSize: { xs: '50px !important', sm: '70px !important' },
                  }}
                />
              </IconButton>
              {currentPage?.title && currentPage?.type === 'list' && (
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '28px !important', sm: '35px !important' },
                    color: theme.palette.common.black,
                  }}
                >
                  {currentPage.title}
                </Typography>
              )}
              {currentPage?.type === 'dashboard' && (
                <Box flex={1}>
                  <DateRangePicker
                    startDate={rangeDate.start}
                    endDate={rangeDate.end}
                    onChange={changeRangeDate}
                    format={DatePattern.DATE_JP}
                  />
                </Box>
              )}
            </Box>
            {currentPage?.type === 'list' && (
              <Box sx={{ padding: 1, paddingTop: 0 }}>
                <DatePicker
                  value={dayjs(date)}
                  closeOnSelect
                  onChange={(newValue: Dayjs | null) => {
                    setDate(dayjs(newValue).format(DatePattern.DATE));
                  }}
                  desktopModeMediaQuery='(min-width:0px)'
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      fullWidth: true,
                      sx: {
                        '& .MuiInputBase-input': {
                          padding: '7.04px 14px',
                          color: '#1a1a1a',
                        },
                      },
                    },
                    toolbar: { hidden: true },
                    actionBar: { actions: [] },
                  }}
                  format={DatePattern.DATE_JP}
                />
              </Box>
            )}
          </Box>
        )}
        {['user_detail', 'visit_detail'].includes(currentPage?.type ?? '') && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={() => {
                  navigate(-1);
                }}
              >
                <ArrowBackIosNewIcon
                  sx={{
                    fill: theme.palette.primary.main,
                    fontSize: { xs: '35px !important', sm: '50px !important' },
                  }}
                />
              </IconButton>
              {currentPage?.title && (
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '28px !important', sm: '35px !important' },
                    color: theme.palette.common.black,
                  }}
                >
                  {currentPage.title}
                </Typography>
              )}
            </Box>
            {dateParams && currentPage?.type === 'user_detail' && (
              <Typography
                component='p'
                sx={{
                  color: '#000',
                  border: '1px solid #bababa',
                  borderRadius: '4px',
                  mr: '10px',
                  padding: '7px 14px',
                }}
              >
                {dayjs(dateParams).format(DatePattern.DATE_JP)}
              </Typography>
            )}
          </Box>
        )}
      </Toolbar>
    </StyledAppBar>
  );
};

export default AppBar;
